import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";

import { commentQueries } from "@/react/lib/queries/comments";
import type { Comment } from "@/react/types/Comment";

interface Preloaded {
  comments: Comment[];
  hasNextPage?: boolean;
  totalCount: number;
}

interface UseCommentListParams {
  parentCommentId?: number;
  postId: number;
  preloaded?: Preloaded;
}

const buildPreloadedInitialData = ({
  comments,
  totalCount,
  hasNextPage,
}: Preloaded) => ({
  pages: [
    {
      records: comments,
      page: 0,
      has_next_page: hasNextPage ?? totalCount > comments.length,
      count: totalCount,
      page_count: 1,
      per_page: comments.length,
    },
  ],
  pageParams: [1],
});

export const useCommentList = ({
  postId,
  parentCommentId,
  preloaded,
}: UseCommentListParams) => {
  const { data, isPending, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      ...commentQueries.list({ postId, parentCommentId }),
      ...(preloaded && {
        initialData: () => buildPreloadedInitialData(preloaded),
      }),
    });

  const comments = useMemo(
    () =>
      uniqBy(
        (data?.pages ?? []).flatMap(page => page.records),
        "id",
      ),
    [data],
  );

  return {
    comments,
    isPending,
    isFetching,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
  };
};
